import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { enqueueRpc } from "../../../lib/enqueueRpc";
import { hasQuantity } from "../../../lib/quantityInput";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { useStockSource } from "../../../hooks/useStockSource";
import { Button } from "../../../components/ui/button";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { QuantityStepper } from "../../../components/mobile/QuantityStepper";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { PositionPicker } from "../../../components/mobile/PositionPicker";
import { ArticlePhotoPreview } from "../../../components/mobile/ArticlePhotoPreview";
import { serialsByPositionFrom } from "../../../lib/serialsByPosition";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

export default function UmlagerungFlow() {
    const flow = useFlowOperationId();
    const prefill = useScanPrefill();
    const [positions, setPositions] = useState([]);
    const [compartments, setCompartments] = useState([]);
    const [articles, setArticles] = useState({});
    const [serialsByPosition, setSerialsByPosition] = useState({});
    const [positionId, setPositionId] = useState("");
    const [toCompartmentId, setToCompartmentId] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [scanRestartKey, setScanRestartKey] = useState(0);
    const compartmentMap = useMemo(() => Object.fromEntries(compartments.map(c => [c.id, c])), [compartments]);
    const { sourcePositions, scanSource, resetSource, sourceLabel } = useStockSource(positions, compartmentMap, prefill, setPositionId);

    useEffect(() => {
        Promise.all([
            rpc.get("/master/stock-positions"),
            rpc.get("/master/compartments"),
            rpc.get("/master/articles"),
            rpc.get("/master/assets?limit=5000"),
        ]).then(([p, c, a, s]) => {
            if (p.ok) setPositions(p.data);
            if (c.ok) setCompartments(c.data);
            if (a.ok) setArticles(Object.fromEntries(a.data.map((x) => [x.id, x])));
            // #182: Seriennummern der Geräte je Position für die Suche
            if (s.ok) setSerialsByPosition(serialsByPositionFrom(s.data));
        });
    }, []);

    const selectedArticle = articles[positions.find((p) => p.id === positionId)?.article_id];

    useEffect(() => {
        if (prefill?.toCompartmentId) setToCompartmentId(prefill.toCompartmentId);
    }, [prefill]);

    const submit = async () => {
        if (!positionId || !toCompartmentId) return toast.error("Quelle und Ziel wählen.");
        if (!hasQuantity(quantity)) return toast.error("Menge eingeben.");
        setSubmitting(true);
        const res = await enqueueRpc({
            path: "/movements",
            rpcName: "Umlagerung",
            summary: `${quantity} Stk`,
            payload: {
                client_operation_id: flow.clientOperationId,
                type: "relocate",
                from_position_id: positionId,
                to_compartment_id: toCompartmentId,
                quantity,
            },
        });
        setSubmitting(false);
        if (!res.ok) {
            if (res.retryable) toast.warning("Offline -- Umlagerung wartet in der Queue.");
            else return toast.error(res.error);
        } else {
            toast.success("Umlagerung gebucht.");
        }
        setPositions(current => current.map(position => position.id === positionId
            ? { ...position, quantity: Math.max(0, position.quantity - quantity) } : position));
        if (res.ok) {
            const refreshed = await rpc.get("/master/stock-positions");
            if (refreshed.ok) setPositions(refreshed.data);
        }
        flow.rotate();
        setPositionId("");
        setQuantity(1);
        setScanRestartKey((key) => key + 1);
    };

    return (
        <FlowShell
            testId="flow-umlagerung"
            title="Umlagerung"
            kind="Schnellflow"
            onSubmit={submit}
            submitDisabled={!positionId || !toCompartmentId || !hasQuantity(quantity)}
            submitting={submitting}
        >
            <ScanBox
                placeholder="Quelle oder Ziel scannen"
                restartKey={scanRestartKey}
                onResolved={(raw, resolved) => {
                    if (resolved?.kind === "compartment") {
                        if (positionId) {
                            setToCompartmentId(resolved.id);
                            toast.success("Ziel-Fach übernommen.");
                        } else {
                            scanSource(resolved);
                        }
                    } else if (resolved?.kind === "article") {
                        scanSource(resolved);
                    } else {
                        toast.error(`Code nicht zugeordnet: ${raw}`);
                    }
                }}
            />
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Quelle</Label>
                {sourceLabel ? <p role="status" className="text-sm">{sourceLabel}</p> : null}
                <Button type="button" variant="outline" className="min-h-11" onClick={() => { resetSource(); setToCompartmentId(""); }}>Andere Quelle wählen / scannen</Button>
                {sourcePositions.length === 0 ? <p role="status">Kein Bestand in dieser Quelle.</p> : null}
                <PositionPicker
                    value={positionId}
                    onChange={setPositionId}
                    positions={sourcePositions}
                    articles={articles}
                    compartments={compartmentMap}
                    serialsByPosition={serialsByPosition}
                    testId="uml-source"
                />
                <ArticlePhotoPreview
                    articleId={selectedArticle?.id}
                    articleName={selectedArticle?.name}
                    testId="uml-article-photo"
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ziel-Fach</Label>
                <Select value={toCompartmentId} onValueChange={setToCompartmentId}>
                    <SelectTrigger className="h-12" data-testid="uml-target">
                        <SelectValue placeholder="Ziel-Fach wählen" />
                    </SelectTrigger>
                    <SelectContent>
                        {compartments.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.name} ({c.code})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Menge</Label>
                <QuantityStepper value={quantity} onChange={setQuantity} min={1} testId="uml-qty" />
            </div>
        </FlowShell>
    );
}
